import { forwardRef, HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  ),
)
Table.displayName = 'Table'

interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  sticky?: boolean
}

const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, sticky, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn('', sticky && 'sticky top-0 z-20 bg-white', className)}
      {...props}
    />
  ),
)
TableHeader.displayName = 'TableHeader'

const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('[&_tr:last-child]:border-0 ', className)} {...props} />
  ),
)
TableBody.displayName = 'TableBody'

interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  noHover?: boolean
  isActive?: boolean
  noGroup?: boolean
}

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, noHover, isActive, noGroup, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b transition-colors',
        {
          group: !noGroup,
          'hover:bg-[#F9FBFF] data-[state=selected]:bg-grey-s1': !(noHover ?? false),
          'bg-blue-s1 border-b-blue-s5 hover:bg-blue-s1': isActive,
        },
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sticky?: boolean
  condensed?: boolean
}

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, sticky, condensed, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'px-4 py-3 text-left text-xs align-middle font-bold [&:has([role=checkbox])]:pr-0',
        'whitespace-nowrap overflow-hidden text-ellipsis',
        sticky &&
          'sticky left-0 z-10 after:absolute after:right-0 after:top-0 after:h-full after:w-[5px] after:shadow-[4px_0_6px_-1px_rgba(0,0,0,0.1),2px_0_4px_-1px_rgba(0,0,0,0.06)]',
        {
          ...(condensed && {
            'h-10 max-h-10 min-h-10 p-4': true,
          }),
        },
        className,
      )}
      {...props}
    />
  ),
)
TableHead.displayName = 'TableHead'

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  sticky?: boolean
  condensed?: boolean
  isActive?: boolean
}

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, sticky, condensed, isActive, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'p-4 align-middle text-xs [&:has([role=checkbox])]:pr-0',
        'h-18 max-h-18 min-h-18 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        {
          'bg-blue-s1 border-b-blue-s5': isActive,
          'group-hover:bg-[#F9FBFF]': !isActive,
        },
        sticky &&
          'sticky z-10 left-0 after:absolute after:right-0 after:top-0 after:h-full after:w-[5px] after:shadow-[4px_0_6px_-1px_rgba(0,0,0,0.1),2px_0_4px_-1px_rgba(0,0,0,0.06)]',
        className,
        {
          ...(condensed && {
            'h-12 max-h-12 min-h-12 py-1 px-4': true,
          }),
        },
      )}
      {...props}
    />
  ),
)
TableCell.displayName = 'TableCell'

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell }
